'use client';

import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Image } from '@lichtblick/photon/atoms/image/image';
import { CrossCircleS } from '@lichtblick/photon/icons/symbols/cross-circle-s';
import { useTracker } from '@lichtblick/tracker';
import classNames from 'classnames';
import { type FC, useCallback, useEffect, useState } from 'react';
import { HotlineModalWithCTA } from '../modals/hotline-modal-with-cta';
import styles from './hotline-toast.module.scss';

const DISPLAY_DELAY_IN_SECONDS = 60;
const OFFICE_HOUR_START = 8;
const OFFICE_HOUR_END = 16;

const isDayOfService = (): boolean => {
  const today = new Date();
  const day = today.getDay();

  // no service on staturday and sunday
  if (day === 6 || day === 0) {
    return false;
  }

  return true;
};

const isTimeOfService = (startHour: number, endHour: number): boolean => {
  const hours = new Date().getHours();

  return hours >= startHour && hours <= endHour;
};

type HotlineToastProps = {
  hideOnMobile?: boolean;
};

export const HotlineToast: FC<HotlineToastProps> = ({ hideOnMobile }) => {
  const [isHidden, setIsHidden] = useState(true);

  const { trackCustomEvent } = useTracker();

  const trackEvent = useCallback(() => {
    trackCustomEvent({
      eventName: 'hotline_teaser_impression',
    });
  }, [trackCustomEvent]);

  useEffect(() => {
    const toastTimeout = DISPLAY_DELAY_IN_SECONDS * 1000;

    if (isDayOfService() && isTimeOfService(OFFICE_HOUR_START, OFFICE_HOUR_END)) {
      setTimeout(() => {
        setIsHidden(false);
        trackEvent();
      }, toastTimeout);
    }
  }, [trackEvent]);

  return (
    <div
      className={classNames(
        styles.root,
        isHidden && styles['root-hidden'],
        hideOnMobile && styles['root-mobile-hidden'],
      )}
      data-testid="toast"
    >
      <div className={styles.toast}>
        <button
          aria-label="Schließen"
          className={styles['close-button']}
          onClick={() => setIsHidden(true)}
          type="button"
        >
          <CrossCircleS />
        </button>

        <Image
          className={styles.image}
          description="Kundensupport"
          url={{
            mobile: '/checkout-b2c/assets/notebook-chat.svg',
          }}
        />

        <Headline className={styles.headline} size="s">
          Können wir dir bei der Bestellung helfen?
        </Headline>

        <HotlineModalWithCTA />
      </div>
    </div>
  );
};
